/**
 * Plan feature derivation shared by the plan surfaces.
 *
 * Returns data, never markup: the desktop comparison card renders these as a
 * single column with icons, the mobile registration list as a two-column grid,
 * and both must agree on which rows exist and how a limit reads.
 */

import { DEFAULT_VOICE_TRIAL_DURATION_HOURS } from '@/lib/billing/constants'
import { formatCredits } from '@/lib/billing/utils/credits'

export type PlanVoiceStatus = 'included' | 'trial' | 'none'

export interface PlanVoiceLabel {
  status: PlanVoiceStatus
  text: string
}

interface PlanVoiceFields {
  voice_included: boolean
  voice_trial_enabled: boolean
  voice_trial_duration_hours: number | null
}

export function getPlanVoiceLabel(plan: PlanVoiceFields): PlanVoiceLabel {
  if (plan.voice_included) {
    return { status: 'included', text: 'Voice Included' }
  }
  if (plan.voice_trial_enabled) {
    const days = Math.round((plan.voice_trial_duration_hours ?? DEFAULT_VOICE_TRIAL_DURATION_HOURS) / 24)
    return { status: 'trial', text: `Voice Trial (${days}d)` }
  }
  return { status: 'none', text: 'No Voice' }
}

export interface PlanCreditFields {
  price_monthly_cents: number
  initial_balance_microusd: number
  payment_to_balance_microusd: number
  payment_to_balance_count: number | null
}

/** Credits a plan adds: the recurring payment grant, or a free plan's starting balance. */
export function getPlanCreditsText(plan: PlanCreditFields): string | null {
  if (plan.price_monthly_cents === 0) {
    return plan.initial_balance_microusd > 0 ? `${formatCredits(plan.initial_balance_microusd)} one-time` : null
  }
  if (plan.payment_to_balance_microusd <= 0) return null
  const credits = formatCredits(plan.payment_to_balance_microusd)
  switch (plan.payment_to_balance_count) {
    case null:
      return `${credits} / month`
    case 1:
      return `${credits} with the first payment`
    default:
      return `${credits} / month for the first ${plan.payment_to_balance_count} payments`
  }
}

export interface PlanCreditLifetimeFields {
  price_monthly_cents: number
  payment_to_balance_microusd: number
  plan_credits_expire: boolean
}

/** How long a paid plan's per-payment credits last; null for plans without them. */
export function getPlanCreditsLifetimeText(plan: PlanCreditLifetimeFields): string | null {
  if (plan.price_monthly_cents === 0 || plan.payment_to_balance_microusd <= 0) return null
  return plan.plan_credits_expire ? 'Unused credits expire at the end of each billing period' : 'Unused credits roll over'
}

export interface PlanLimitRow {
  label: string
  value: number | null
  unit?: string
  /** The Shopify-stores row is the only one that carries the Insights trial hint. */
  shopifyStores?: boolean
}

interface PlanLimitFields {
  voice_included: boolean
  voice_trial_enabled: boolean
  voice_agents_limit: number
  campaigns_enabled?: boolean
  campaigns_limit?: number | null
  pbx_numbers_limit: number | null
  knowledge_bases_limit: number | null
  knowledge_bases_size_mb_limit: number | null
  users_limit: number | null
  crm_integrations_limit: number | null
  shopify_stores_limit: number | null
}

/**
 * A limit of 0 means "not included" → the row is dropped (null = unlimited, so
 * it stays and reads as "Unlimited"). Shopify stores follow the same value-based
 * rule: the capability is decoupled from billing_channel, so gate on the limit,
 * not the channel.
 *
 */
export function getPlanLimitRows(plan: PlanLimitFields): PlanLimitRow[] {
  return [
    // Text agents are not a plan limit; every plan includes them.
    { label: 'text agents', value: null },
    // Without voice the count is moot: next to "No Voice" it would read as an offer.
    ...(plan.voice_included || plan.voice_trial_enabled
      ? [{ label: 'Voice agents', value: plan.voice_agents_limit }]
      : []),
    ...(plan.campaigns_enabled === false
      ? []
      : [{ label: 'Campaigns', value: plan.campaigns_limit ?? null }]),
    { label: 'Phone numbers', value: plan.pbx_numbers_limit },
    { label: 'Knowledge base', value: plan.knowledge_bases_size_mb_limit, unit: 'MB' },
    { label: 'Users', value: plan.users_limit },
    { label: 'CRM integrations', value: plan.crm_integrations_limit },
    { label: 'Shopify stores', value: plan.shopify_stores_limit, shopifyStores: true },
  ].filter(({ value }) => value !== 0)
}

export function formatPlanLimitRow({ label, value, unit }: PlanLimitRow): string {
  if (value === null || value === undefined) return `Unlimited ${label}`
  return unit ? `${value} ${unit} ${label}` : `${value} ${label}`
}

/**
 * Shopify Insights trial length. A positive duration is a time-limited trial
 * worth surfacing; null/absent means included without limit, so there is
 * nothing to say.
 */
export function getInsightsTrialText(hours: number | null | undefined): string | null {
  if (typeof hours !== 'number' || hours <= 0) return null
  return hours < 24 ? `${hours}h` : `${Math.round(hours / 24)}d`
}

export function getPlanBenefits(benefits: string[] | unknown): string[] {
  return Array.isArray(benefits) ? (benefits as string[]) : []
}
